import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { theme } from './theme';
import UserDetailModal from './UserDetailModal';
import ActividadAdmins from './ActividadAdmins';
import { usePermisoOperaciones } from './permisos';
import './office_panel.css';

const GET_OFFICE_USERS = gql`query { getOfficeUsers { id email username role createdAt mustChangePassword inNawi officeEnabled operaciones } }`;
const GET_NAWI_USERS = gql`query { getNawiChatUsers { id email name role mustChangePassword inOffice createdAt } }`;
const CREATE_OFFICE_USER = gql`mutation CreateOfficeUser($email: String!, $role: String!) { createOfficeUser(email: $email, role: $role) { id email role } }`;
const DEACTIVATE_OFFICE_USER = gql`mutation DeactivateOfficeUser($userId: ID!) { deactivateOfficeUser(userId: $userId) }`;
const DEACTIVATE_NAWI_USER = gql`mutation DeactivateNawiChatUser($id: ID!) { deactivateNawiChatUser(id: $id) }`;
const GET_DEACTIVATED_NAWI_USERS = gql`query { getDeactivatedNawiChatUsers { id email name role mustChangePassword inOffice createdAt } }`;
const REACTIVATE_NAWI_USER = gql`mutation ReactivateNawiChatUser($id: ID!) { reactivateNawiChatUser(id: $id) }`;
const UPDATE_USER_ROLE = gql`mutation UpdateUserRole($userId: ID!, $role: String!) { updateUserRole(userId: $userId, role: $role) { id email role } }`;
const TOGGLE_OFFICE = gql`mutation ToggleOfficeAccess($userId: ID!, $enabled: Boolean!) { toggleOfficeAccess(userId: $userId, enabled: $enabled) { id officeEnabled } }`;
const SET_OPERACIONES = gql`mutation SetPermisoOperaciones($userId: ID!, $habilitado: Boolean!) { setPermisoOperaciones(userId: $userId, habilitado: $habilitado) { id operaciones } }`;

const ROLES = [
  { value: 'superadmin', label: 'Superadmin', color: '#7c3aed', bg: '#f3e8ff' },
  { value: 'admin', label: 'Admin', color: '#0070f3', bg: '#e0f2fe' },
  { value: 'user', label: 'Usuario', color: '#059669', bg: '#ecfdf5' },
];

export default function AdminPanel() {
  const { user, isSuperadmin, logout } = useAuth();
  const { operaciones: tengoOperaciones, refetch: refetchPermiso } = usePermisoOperaciones();
  const navigate = useNavigate();
  const [tab, setTab] = useState('office');
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('admin');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [editRoleId, setEditRoleId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showBlockedChat, setShowBlockedChat] = useState(false);
  const [detailUser, setDetailUser] = useState(null); // { id, type: 'office' | 'nawi' }

  const { data: officeData, loading: officeLoading, refetch: refetchOffice } = useQuery(GET_OFFICE_USERS);
  const { data: nawiData, loading: nawiLoading, refetch: refetchNawi } = useQuery(GET_NAWI_USERS);
  const { data: blockedNawiData, loading: blockedNawiLoading, refetch: refetchBlockedNawi } = useQuery(
    GET_DEACTIVATED_NAWI_USERS,
    { skip: !(tab === 'nawi' && showBlockedChat) }
  );
  const [createUser, { loading: creating }] = useMutation(CREATE_OFFICE_USER);
  const [updateRole] = useMutation(UPDATE_USER_ROLE);
  const [deactivateOfficeMut] = useMutation(DEACTIVATE_OFFICE_USER);
  const [deactivateNawiMut] = useMutation(DEACTIVATE_NAWI_USER);
  const [reactivateNawiMut] = useMutation(REACTIVATE_NAWI_USER);
  const [toggleOfficeMut] = useMutation(TOGGLE_OFFICE);
  const [setOperacionesMut] = useMutation(SET_OPERACIONES);

  const flash = (msg, type = 'success') => {
    setToast({ show: true, message: msg, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    try {
      await createUser({ variables: { email: newEmail.trim(), role: newRole } });
      flash(`Cuenta creada. Mail enviado a ${newEmail.trim()}`);
      setNewEmail(''); setShowCreate(false);
      refetchOffice(); refetchNawi();
    } catch (err) { flash(err.message, 'error'); }
  };

  const handleDeactivate = async (id, type) => {
    try {
      if (type === 'office') await deactivateOfficeMut({ variables: { userId: id } });
      else await deactivateNawiMut({ variables: { id } });
      flash(type === 'office' ? 'Acceso al panel bloqueado.' : 'Acceso al chat bloqueado.');
      setDeleteId(null);
      refetchOffice(); refetchNawi();
    } catch (err) { flash(err.message, 'error'); }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await updateRole({ variables: { userId, role } });
      flash('Rol actualizado.');
      setEditRoleId(null); refetchOffice();
    } catch (err) { flash(err.message, 'error'); }
  };

  const handleToggleOffice = async (userId, enabled) => {
    try {
      await toggleOfficeMut({ variables: { userId, enabled } });
      flash(enabled ? 'Acceso Office habilitado.' : 'Acceso Office deshabilitado.');
      refetchOffice();
    } catch (err) { flash(err.message, 'error'); }
  };

  // Permiso Operaciones (ve los agentes): solo lo da o lo quita quien ya lo tiene.
  const handleOperaciones = async (userId, habilitado) => {
    try {
      await setOperacionesMut({ variables: { userId, habilitado } });
      flash(habilitado ? 'Permiso Operaciones otorgado.' : 'Permiso Operaciones quitado.');
      refetchOffice(); refetchPermiso();
    } catch (err) { flash(err.message, 'error'); }
  };

  const handleReactivateNawi = async (id) => {
    try {
      await reactivateNawiMut({ variables: { id } });
      flash('Usuario desbloqueado.');
      refetchBlockedNawi(); refetchNawi();
    } catch (err) { flash(err.message, 'error'); }
  };

  if (!isSuperadmin) {
    return (
      <div className="page-container" style={{ maxWidth: 900 }}>
        <div className="panel-card" style={{ padding: 40, textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 8 }}>Acceso denegado</h2>
          <p style={{ color: '#9ca3af', marginBottom: 16 }}>Solo el superadmin puede acceder.</p>
          <button className="btn-accent" onClick={() => navigate('/altaEmpresa')}>Volver</button>
        </div>
      </div>
    );
  }

  const officeUsers = officeData?.getOfficeUsers || [];
  const nawiUsers = nawiData?.getNawiChatUsers || [];

  const getRoleBadge = (role) => {
    const r = ROLES.find(x => x.value === role) || ROLES[2];
    return { fontSize: '0.68rem', background: r.bg, color: r.color, padding: '2px 8px', borderRadius: '10px', fontWeight: '600', border: `1px solid ${r.color}22` };
  };

  const filterUsers = (list) => {
    if (!searchTerm.trim()) return list;
    const t = searchTerm.toLowerCase();
    return list.filter(u => (u.email || '').toLowerCase().includes(t) || (u.name || '').toLowerCase().includes(t));
  };

  const filteredOffice = filterUsers(officeUsers);
  const filteredNawi = filterUsers(nawiUsers);

  return (
    <div className="page-container" style={{ maxWidth: 900 }}>
      <div className={`toast toast--${toast.type === 'error' ? 'error' : 'success'} ${toast.show ? 'toast--show' : ''}`}>
        {toast.type === 'error' ? '⚠️' : '✅'} {toast.message}
      </div>

      {/* Header */}
      <div className="office-header">
        <div className="office-header-left">
          <button className="btn-subtle" onClick={() => navigate('/altaEmpresa')}>← Volver</button>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 700, letterSpacing: '-0.3px', margin: 0 }}>Administrar Usuarios</h1>
        </div>
        <button className="btn-subtle" onClick={() => logout()}>Cerrar sesión</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid #ecedf0' }}>
        <button onClick={() => { setTab('office'); setDeleteId(null); setEditRoleId(null); setSearchTerm(''); }}
          style={{ padding: '10px 20px', fontWeight: 600, fontSize: '0.9rem', border: 'none', background: 'none', cursor: 'pointer',
            color: tab === 'office' ? '#0070f3' : '#9ca3af', borderBottom: tab === 'office' ? '2px solid #0070f3' : '2px solid transparent',
            marginBottom: '-2px', transition: 'all 0.15s' }}>
          Office <span style={{ fontSize: '0.72rem', background: '#e0f2fe', color: '#0070f3', padding: '1px 6px', borderRadius: 8, marginLeft: 6 }}>{officeUsers.length}</span>
        </button>
        <button onClick={() => { setTab('nawi'); setDeleteId(null); setEditRoleId(null); setSearchTerm(''); }}
          style={{ padding: '10px 20px', fontWeight: 600, fontSize: '0.9rem', border: 'none', background: 'none', cursor: 'pointer',
            color: tab === 'nawi' ? '#0ea5e9' : '#9ca3af', borderBottom: tab === 'nawi' ? '2px solid #0ea5e9' : '2px solid transparent',
            marginBottom: '-2px', transition: 'all 0.15s' }}>
          NAWI Chat <span style={{ fontSize: '0.72rem', background: '#e0f2fe', color: '#0ea5e9', padding: '1px 6px', borderRadius: 8, marginLeft: 6 }}>{nawiUsers.length}</span>
        </button>
        <button onClick={() => { setTab('actividad'); setDeleteId(null); setEditRoleId(null); setSearchTerm(''); }}
          style={{ padding: '10px 20px', fontWeight: 600, fontSize: '0.9rem', border: 'none', background: 'none', cursor: 'pointer',
            color: tab === 'actividad' ? '#7c3aed' : '#9ca3af', borderBottom: tab === 'actividad' ? '2px solid #7c3aed' : '2px solid transparent',
            marginBottom: '-2px', transition: 'all 0.15s' }}>
          Actividad
        </button>
      </div>

      {/* Create user (office tab only) */}
      {tab === 'office' && (
        <div className="panel-card" style={{ padding: 20, marginBottom: 16 }}>
          {!showCreate ? (
            <button className="btn-accent" onClick={() => setShowCreate(true)} style={{ width: '100%' }}>+ Crear cuenta Office</button>
          ) : (
            <form onSubmit={handleCreate} className="fade-in">
              <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 12 }}>Nueva cuenta Office</h3>
              <span className="manual-label">Email</span>
              <input className="manual-input" type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="usuario@empresa.com" required autoFocus />
              <span className="manual-label">Rol</span>
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {ROLES.map(r => (
                  <button key={r.value} type="button" onClick={() => setNewRole(r.value)}
                    style={{ padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem',
                      background: newRole === r.value ? r.color : '#f5f5f5', color: newRole === r.value ? '#fff' : '#666',
                      border: newRole === r.value ? `2px solid ${r.color}` : '2px solid #e0e0e0', transition: 'all 0.15s' }}>
                    {r.label}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-outline" type="button" onClick={() => setShowCreate(false)}>Cancelar</button>
                <button className="btn-accent" type="submit" disabled={creating}>{creating ? 'Creando...' : 'Crear y enviar mail'}</button>
              </div>
            </form>
          )}
        </div>
      )}

      {tab === 'actividad' && <ActividadAdmins />}

      {/* User list */}
      {tab !== 'actividad' && (
      <div className="panel-card" style={{ padding: 20 }}>
        <input className="search-bar" placeholder="Buscar por email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ marginBottom: 12 }} />

        {/* ─── OFFICE TAB ─── */}
        {tab === 'office' && (
          officeLoading ? <div className="spinner" /> :
          filteredOffice.length === 0 ? <div className="empty-state">Sin usuarios Office.</div> :
          <div className="player-scroll">
            {filteredOffice.map(u => {
              // Deactivate confirm replaces entire row
              if (deleteId === u.id) {
                return (
                  <div key={u.id} className="del-confirm" style={{ margin: '4px 0', padding: 14, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10 }}>
                    <p style={{ fontWeight: 700, marginBottom: 8 }}>Bloquear acceso al panel de "{u.email}"</p>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>
                      El usuario ya no podrá entrar al panel administrativo. Sus datos y todo lo que hizo se conservan.
                    </p>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>
                      Podés desbloquearlo en cualquier momento.
                    </p>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button className="btn-outline" onClick={() => setDeleteId(null)}>Cancelar</button>
                      <button
                        onClick={() => handleDeactivate(u.id, 'office')}
                        style={{ background: '#f59e0b', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                        Bloquear acceso
                      </button>
                    </div>
                  </div>
                );
              }
              return (
              <div key={u.id} className="player-item" style={{ cursor: 'default' }}>
                <div className="player-info" style={{ cursor: 'pointer' }}
                  onClick={() => setDetailUser({ id: u.id, type: 'office' })}>
                  <div className="player-name">
                    {u.email}
                    <span style={getRoleBadge(u.role)}>{(u.role || 'admin').toUpperCase()}</span>
                    {u.operaciones && <span className="tag tag--purple" style={{ fontSize: '0.6rem' }} title="Ve y maneja los agentes">Operaciones</span>}
                    {u.inNawi && <span className="tag tag--green" style={{ fontSize: '0.6rem' }}>NAWI</span>}
                    {!u.inNawi && <span className="tag tag--gray" style={{ fontSize: '0.6rem' }}>Solo Office</span>}
                    {u.mustChangePassword && <span className="tag tag--yellow" style={{ fontSize: '0.6rem' }}>Pass pendiente</span>}
                  </div>
                  <div className="player-meta">
                    Creado: {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '-'}
                    <span style={{ marginLeft: 8 }}>
                      · Panel: <button onClick={(e) => { e.stopPropagation(); handleToggleOffice(u.id, !u.officeEnabled); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem',
                          color: u.officeEnabled ? '#18794e' : '#dc2626' }}>
                        {u.officeEnabled ? '✓ Habilitado' : '✗ Bloqueado'}
                      </button>
                    </span>
                    {tengoOperaciones && u.role === 'superadmin' && (
                      <span style={{ marginLeft: 8 }}>
                        · Operaciones: <button onClick={(e) => { e.stopPropagation(); handleOperaciones(u.id, !u.operaciones); }}
                          title={u.operaciones ? 'Quitarle el permiso de ver y manejar los agentes' : 'Darle el permiso de ver y manejar los agentes'}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem',
                            color: u.operaciones ? '#7c3aed' : '#9ca3af' }}>
                          {u.operaciones ? '✓ Sí' : '✗ No'}
                        </button>
                      </span>
                    )}
                  </div>
                </div>

                {editRoleId === u.id ? (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {ROLES.map(r => (
                      <button key={r.value} onClick={(e) => { e.stopPropagation(); handleRoleChange(u.id, r.value); }}
                        style={{ padding: '3px 8px', borderRadius: 6, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600,
                          background: r.bg, color: r.color, border: `1px solid ${r.color}44` }}>{r.label}</button>
                    ))}
                    <button onClick={(e) => { e.stopPropagation(); setEditRoleId(null); }} style={{ padding: '3px 6px', borderRadius: 6, background: 'none', border: '1px solid #ddd', cursor: 'pointer', fontSize: '0.72rem', color: '#999' }}>✕</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button onClick={(e) => { e.stopPropagation(); setEditRoleId(u.id); }} style={{ background: 'none', border: 'none', color: '#0070f3', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 500 }}>Rol</button>
                    <button className="player-remove" onClick={(e) => { e.stopPropagation(); setDeleteId(u.id); }} title="Bloquear acceso al panel">×</button>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}

        {/* ─── NAWI CHAT TAB ─── */}
        {tab === 'nawi' && (
          <>
            {/* Toggle bloqueados */}
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, fontSize: '0.85rem', color: '#6b7280', cursor: 'pointer' }}>
              <input type="checkbox" checked={showBlockedChat} onChange={e => setShowBlockedChat(e.target.checked)} />
              Mostrar bloqueados
            </label>

            {showBlockedChat ? (
              /* Modo BLOQUEADOS — botón verde Desbloquear, sin "×" */
              blockedNawiLoading ? <div className="spinner" /> :
              filterUsers(blockedNawiData?.getDeactivatedNawiChatUsers || []).length === 0 ?
                <div className="empty-state">Sin usuarios bloqueados.</div> :
              <div className="player-scroll">
                {filterUsers(blockedNawiData?.getDeactivatedNawiChatUsers || []).map(u => (
                  <div key={u.id} className="player-item" style={{ cursor: 'default' }}>
                    <div className="player-info" style={{ cursor: 'pointer' }}
                      onClick={() => setDetailUser({ id: u.id, type: 'nawi' })}>
                      <div className="player-name">
                        {u.email || 'Sin email'}
                        {u.name && <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 400 }}>({u.name})</span>}
                        <span className="tag" style={{ fontSize: '0.6rem', background: '#e5e7eb', color: '#6b7280', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>Bloqueado</span>
                      </div>
                      <div className="player-meta">
                        Rol: {u.role || 'user'} · Creado: {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '-'}
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleReactivateNawi(u.id); }}
                      style={{ background: '#059669', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                      Desbloquear
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              /* Modo ACTIVOS — exactamente como hoy */
              nawiLoading ? <div className="spinner" /> :
              filteredNawi.length === 0 ? <div className="empty-state">Sin usuarios NAWI Chat.</div> :
              <div className="player-scroll">
                {filteredNawi.map(u => {
              if (deleteId === u.id) {
                return (
                  <div key={u.id} className="del-confirm" style={{ margin: '4px 0', padding: 14, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10 }}>
                    <p style={{ fontWeight: 700, marginBottom: 8 }}>Bloquear acceso al chat de "{u.name || u.email}"</p>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>
                      El usuario ya no podrá entrar a NAWI Chat ni ver sus conversaciones. Sus chats, perfiles y datos se conservan.
                    </p>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>
                      Podés desbloquearlo en cualquier momento.
                    </p>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button className="btn-outline" onClick={() => setDeleteId(null)}>Cancelar</button>
                      <button
                        onClick={() => handleDeactivate(u.id, 'nawi')}
                        style={{ background: '#f59e0b', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                        Bloquear acceso
                      </button>
                    </div>
                  </div>
                );
              }
              return (
              <div key={u.id} className="player-item" style={{ cursor: 'default' }}>
                <div className="player-info" style={{ cursor: 'pointer' }}
                  onClick={() => setDetailUser({ id: u.id, type: 'nawi' })}>
                  <div className="player-name">
                    {u.email || 'Sin email'}
                    {u.name && <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 400 }}>({u.name})</span>}
                    {u.inOffice && <span className="tag tag--blue" style={{ fontSize: '0.6rem' }}>Office</span>}
                    {!u.inOffice && <span className="tag tag--gray" style={{ fontSize: '0.6rem' }}>Solo NAWI</span>}
                    {u.mustChangePassword && <span className="tag tag--yellow" style={{ fontSize: '0.6rem' }}>Pass pendiente</span>}
                  </div>
                  <div className="player-meta">
                    Rol: {u.role || 'user'} · Creado: {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '-'}
                  </div>
                </div>

                <button className="player-remove" onClick={(e) => { e.stopPropagation(); setDeleteId(u.id); }} title="Bloquear acceso al chat">×</button>
              </div>
              );
            })}
              </div>
            )}
          </>
        )}
      </div>
      )}

      {detailUser && (
        <UserDetailModal
          userId={detailUser.id}
          type={detailUser.type}
          currentUserId={user?.id}
          onClose={() => setDetailUser(null)}
          onFlash={flash}
          onChanged={() => { refetchOffice(); refetchNawi(); if (showBlockedChat) refetchBlockedNawi(); }}
        />
      )}
    </div>
  );
}